import { useRef, useState } from "react";
import { UploadCloud, Send, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

export default function EmbeddedRFQ({ productName }) {
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", country: "",
    quantity: "", dimensions: "", application: "", message: "", website: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const fd = new FormData();
      Object.entries({ ...form, product: productName }).forEach(([k, v]) => fd.append(k, v));
      if (fileRef.current?.files?.[0]) fd.append("file", fileRef.current.files[0]);
      const { data } = await api.post("/rfq", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.ok) {
        setDone(true);
        trackEvent("quote_form_submit", { product: productName, source: "product_page" });
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="border border-emerald-400/30 bg-emerald-400/10 p-8 text-center" data-testid="embedded-rfq-success">
        <CheckCircle2 size={40} className="mx-auto text-emerald-400" />
        <p className="mt-4 font-display text-xl font-medium text-white">Thank you.</p>
        <p className="mx-auto mt-2 max-w-md text-sm text-slate-400">
          Your requirement for the {productName} has been received. Our team will review the details and contact you shortly.
        </p>
      </div>
    );
  }

  const dark = "input-field !border-white/15 !bg-white/5 !text-white placeholder:!text-slate-500";

  return (
    <form onSubmit={submit} data-testid="embedded-rfq-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="erfq-name" className="label-field !text-slate-400">Name *</label>
          <input id="erfq-name" data-testid="erfq-name" required className={dark} value={form.name} onChange={set("name")} />
        </div>
        <div>
          <label htmlFor="erfq-company" className="label-field !text-slate-400">Company</label>
          <input id="erfq-company" data-testid="erfq-company" className={dark} value={form.company} onChange={set("company")} />
        </div>
        <div>
          <label htmlFor="erfq-email" className="label-field !text-slate-400">Email *</label>
          <input id="erfq-email" data-testid="erfq-email" type="email" required className={dark} value={form.email} onChange={set("email")} />
        </div>
        <div>
          <label htmlFor="erfq-phone" className="label-field !text-slate-400">Phone / WhatsApp</label>
          <input id="erfq-phone" data-testid="erfq-phone" type="tel" className={dark} value={form.phone} onChange={set("phone")} />
        </div>
        <div>
          <label htmlFor="erfq-country" className="label-field !text-slate-400">Country</label>
          <input id="erfq-country" data-testid="erfq-country" className={dark} value={form.country} onChange={set("country")} />
        </div>
        <div>
          <label htmlFor="erfq-product" className="label-field !text-slate-400">Product</label>
          <input id="erfq-product" data-testid="erfq-product" readOnly className={`${dark} opacity-70`} value={productName} />
        </div>
        <div>
          <label htmlFor="erfq-quantity" className="label-field !text-slate-400">Quantity</label>
          <input id="erfq-quantity" data-testid="erfq-quantity" className={dark} value={form.quantity} onChange={set("quantity")} />
        </div>
        <div>
          <label htmlFor="erfq-dimensions" className="label-field !text-slate-400">Required Dimensions</label>
          <input id="erfq-dimensions" data-testid="erfq-dimensions" className={dark} placeholder="e.g. 700 × 700 × 700 mm" value={form.dimensions} onChange={set("dimensions")} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="erfq-application" className="label-field !text-slate-400">Application</label>
          <input id="erfq-application" data-testid="erfq-application" className={dark} placeholder="e.g. material transfer between Grade C and Grade D areas" value={form.application} onChange={set("application")} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="erfq-message" className="label-field !text-slate-400">Additional Requirements</label>
          <textarea id="erfq-message" data-testid="erfq-message" rows={3} className={dark} placeholder="Interlock type, UV lamp, HMI, wall thickness, documentation needs…" value={form.message} onChange={set("message")} />
        </div>
        <div className="sm:col-span-2">
          <label className="label-field !text-slate-400" htmlFor="erfq-file">Drawing / Specification (optional)</label>
          <label htmlFor="erfq-file" data-testid="erfq-file-label"
            className="flex cursor-pointer items-center gap-3 border border-dashed border-white/20 bg-white/5 p-4 text-sm text-slate-400 transition-colors hover:border-sky-400/50">
            <UploadCloud size={18} className="shrink-0 text-sky-400" />
            {fileName || "PDF, JPG, PNG, DWG, DOC — up to 10 MB"}
            <input id="erfq-file" ref={fileRef} type="file" className="sr-only" accept=".pdf,.jpg,.jpeg,.png,.dwg,.doc,.docx"
              onChange={(e) => setFileName(e.target.files?.[0]?.name || "")} />
          </label>
        </div>
        <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true"
          className="absolute -left-[9999px] h-0 w-0 opacity-0" />
      </div>
      {error && <p className="mt-4 border border-red-400/30 bg-red-400/10 p-3 text-sm text-red-300" data-testid="erfq-error">{error}</p>}
      <button type="submit" data-testid="erfq-submit" disabled={sending}
        className="mt-6 inline-flex items-center gap-2 bg-accent px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-dark disabled:opacity-60">
        {sending ? "Submitting…" : "Submit Requirement"} <Send size={14} />
      </button>
    </form>
  );
}
